// src/pages/AdminPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useThemePalette } from '../hooks/useThemePalette';
import { useThemeStore } from '../stores/useThemeStore';
import { useAuthStore } from '../stores/useAuthStore';

import AdminDashboard    from './admin/AdminDashboard';
import AdminBooks        from './admin/AdminBooks';
import AdminUnitPages    from './admin/AdminUnitPages';
import AdminTests        from './admin/AdminTests';
import AdminMedia        from './admin/AdminMedia';
import AdminSiteContent  from './admin/AdminSiteContent';
import AdminTeacherBooks from './admin/AdminTeacherBooks';

type SectionId = 'dashboard' | 'books' | 'unitPages' | 'tests' | 'media' | 'siteContent' | 'teacherBooks';

const NAV: { id: SectionId; label: string; icon: string }[] = [
  { id: 'dashboard',    label: 'Dashboard',       icon: '▦'  },
  { id: 'books',        label: 'Manage Books',     icon: '≡'  },
  { id: 'unitPages',    label: 'Unit Pages',       icon: '⊞'  },
  { id: 'tests',        label: 'Manage Tests',     icon: '✐'  },
  { id: 'media',        label: 'Media Manager',    icon: '♪'  },
  { id: 'siteContent',  label: 'Site Content',     icon: '✎'  },
  { id: 'teacherBooks', label: "Teacher's Books",  icon: '📚' },
];

type RoleStatus = 'loading' | 'admin' | 'denied';

export default function AdminPage() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';
  const user   = useAuthStore(s => s.user);
  const navigate = useNavigate();

  const [roleStatus,     setRoleStatus]     = useState<RoleStatus>('loading');
  const [activeSection,  setActiveSection]  = useState<SectionId>('dashboard');
  const [sidebarOpen,    setSidebarOpen]    = useState(true);
  const [navHover,       setNavHover]       = useState<SectionId | null>(null);

  useEffect(() => {
    if (!user) { setRoleStatus('denied'); return; }
    getDoc(doc(db, 'users', user.uid))
      .then(snap => {
        setRoleStatus(snap.exists() && snap.data()?.role === 'admin' ? 'admin' : 'denied');
      })
      .catch(() => setRoleStatus('denied'));
  }, [user]);

  if (roleStatus === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg }}>
        <div style={{ color: C.muted, fontSize: '0.88rem' }}>Checking permissions...</div>
      </div>
    );
  }

  if (roleStatus === 'denied') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg, gap: '14px' }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.error} strokeWidth="1.5" strokeLinecap="round">
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        <div style={{ fontSize: '1.1rem', fontWeight: 800, color: C.text }}>Access Denied</div>
        <div style={{ fontSize: '0.82rem', color: C.muted }}>
          {!user ? 'You must be logged in as an admin.' : 'Your account does not have admin privileges.'}
        </div>
        <button
          onClick={() => navigate('/')}
          style={{ padding: '10px 26px', borderRadius: '999px', border: 'none', background: C.accent, color: '#fff', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: '8px' }}
        >
          ← Back to Site
        </button>
      </div>
    );
  }

  // ── Layout ────────────────────────────────────────────────────────────────────
  const SIDEBAR_W = sidebarOpen ? 224 : 56;

  let content: React.ReactNode;
  switch (activeSection) {
    case 'dashboard':    content = <AdminDashboard />;    break;
    case 'books':        content = <AdminBooks />;        break;
    case 'unitPages':    content = <AdminUnitPages />;    break;
    case 'tests':        content = <AdminTests />;        break;
    case 'media':        content = <AdminMedia />;        break;
    case 'siteContent':  content = <AdminSiteContent />;  break;
    case 'teacherBooks': content = <AdminTeacherBooks />; break;
  }

  const sidebarBg  = isDark ? 'rgba(8,12,20,0.98)' : 'rgba(248,250,252,0.98)';
  const headerBg   = isDark ? 'rgba(13,18,32,0.95)' : 'rgba(255,255,255,0.95)';

  return (
    <div style={{ display: 'flex', height: '100vh', background: C.bg, overflow: 'hidden' }}>

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <div style={{
        width:        SIDEBAR_W,
        flexShrink:   0,
        background:   sidebarBg,
        borderRight:  `1px solid ${C.border}`,
        display:      'flex',
        flexDirection:'column',
        transition:   'width 0.22s ease',
        overflow:     'hidden',
      }}>
        {/* Logo row */}
        <div style={{
          padding:       '18px 12px 14px',
          display:       'flex',
          alignItems:    'center',
          gap:           '10px',
          borderBottom:  `1px solid ${C.border}`,
          flexShrink:    0,
          minHeight:     '56px',
        }}>
          <button
            onClick={() => setSidebarOpen(o => !o)}
            style={{
              width: 32, height: 32, borderRadius: '8px',
              border: `1px solid ${C.border}`, background: 'transparent',
              cursor: 'pointer', flexShrink: 0, color: C.muted,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1rem', fontFamily: 'inherit', transition: 'border-color 0.15s ease',
            }}
          >
            {sidebarOpen ? '‹' : '›'}
          </button>
          {sidebarOpen && (
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 900, color: C.text, letterSpacing: '0.1em' }}>ADMIN</div>
              <div style={{ fontSize: '0.62rem', color: C.muted, marginTop: '1px' }}>ESP for Sports</div>
            </div>
          )}
        </div>

        {/* Nav items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 8px' }}>
          {NAV.map(item => {
            const isActive = activeSection === item.id;
            const isHov    = navHover === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                onMouseEnter={() => setNavHover(item.id)}
                onMouseLeave={() => setNavHover(null)}
                title={!sidebarOpen ? item.label : undefined}
                style={{
                  width:          '100%',
                  display:        'flex',
                  alignItems:     'center',
                  gap:            '10px',
                  padding:        sidebarOpen ? '9px 12px' : '9px',
                  justifyContent: sidebarOpen ? 'flex-start' : 'center',
                  borderRadius:   '10px',
                  border:         'none',
                  background:     isActive
                    ? (isDark ? 'rgba(79,140,247,0.14)' : 'rgba(79,140,247,0.08)')
                    : isHov
                      ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')
                      : 'transparent',
                  color:          isActive ? C.accent : C.muted,
                  cursor:         'pointer',
                  marginBottom:   '2px',
                  fontFamily:     'inherit',
                  transition:     'background 0.14s ease, color 0.14s ease',
                }}
              >
                <span style={{ fontSize: sidebarOpen ? '1rem' : '1.1rem', flexShrink: 0, lineHeight: 1 }}>{item.icon}</span>
                {sidebarOpen && (
                  <span style={{ fontSize: '0.8rem', fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap' }}>
                    {item.label}
                  </span>
                )}
                {sidebarOpen && isActive && (
                  <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: C.accent, flexShrink: 0 }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Back to site */}
        <div style={{ padding: '10px 8px 14px', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
          <button
            onClick={() => navigate('/')}
            title={!sidebarOpen ? 'Back to Site' : undefined}
            style={{
              width:          '100%',
              display:        'flex',
              alignItems:     'center',
              gap:            '10px',
              padding:        sidebarOpen ? '9px 12px' : '9px',
              justifyContent: sidebarOpen ? 'flex-start' : 'center',
              borderRadius:   '10px',
              border:         `1px solid ${C.border}`,
              background:     'transparent',
              color:          C.muted,
              cursor:         'pointer',
              fontFamily:     'inherit',
            }}
          >
            <span style={{ fontSize: '0.9rem' }}>←</span>
            {sidebarOpen && <span style={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>Back to Site</span>}
          </button>
        </div>
      </div>

      {/* ── Main ────────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Top bar */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          padding:        '0 28px',
          minHeight:      '56px',
          borderBottom:   `1px solid ${C.border}`,
          background:     headerBg,
          backdropFilter: 'blur(20px)',
          flexShrink:     0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.accent, boxShadow: `0 0 8px ${C.accent}`, flexShrink: 0 }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: C.text }}>
              {NAV.find(n => n.id === activeSection)?.label}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: C.muted, fontWeight: 500 }}>{user?.email}</div>
        </div>

        {/* Scrollable section content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {content}
        </div>
      </div>
    </div>
  );
}
